import { Archive, ArrowRight, MessageSquare, Mic, Puzzle, Sparkles, X, Zap } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils/cn';
import { Button } from './button';
import { Dialog } from './dialog';

type Feature = {
  icon: React.ReactNode;
  title: string;
  description: string;
};

const FEATURES: Feature[] = [
  {
    icon: <MessageSquare className="w-4 h-4" />,
    title: 'Unlimited chats',
    description:
      'No more limits. Start new conversations whenever you need — research, writing, coding, brainstorming, anything.',
  },
  {
    icon: <Mic className="w-4 h-4" />,
    title: 'Speech-to-Text & Text-to-Speech',
    description:
      'Talk to the AI and listen to responses naturally. Perfect for hands-free work, note taking, and long sessions.',
  },
  {
    icon: <Zap className="w-4 h-4" />,
    title: 'Prompt Library',
    description:
      'Generate powerful prompts using configurable parameters. Build, reuse, and refine prompt templates instead of starting from scratch every time.',
  },
  {
    icon: <Archive className="w-4 h-4" />,
    title: 'Chat archiving',
    description:
      'Keep your important conversations organized and accessible. Archive useful threads and return to them whenever needed.',
  },
  {
    icon: <Puzzle className="w-4 h-4" />,
    title: 'MCP integrations',
    description:
      'Connect the assistant to external tools and services through MCP for a more powerful and extensible workflow.',
  },
];

type GuestUpgradeModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const GuestUpgradeModal: React.FC<GuestUpgradeModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  const handleProceed = () => {
    onClose();
    navigate('/signin');
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      aria-labelledby="upgrade-modal-title"
      className="max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
    >
        {/* Close button */}
        <Button
          onClick={onClose}
          variant="ghost"
          size="icon"
          aria-label="Close"
          className="absolute top-4 right-4 z-10"
        >
          <X className="w-4 h-4" />
        </Button>

        {/* Header */}
        <div className="flex flex-col items-center px-8 pt-8 pb-6 text-center">
          <div className="relative mb-5">
            <div
              className="absolute -inset-3 rounded-full bg-primary/15 animate-pulse"
              style={{ animationDuration: '2s' }}
            />
            <div className="relative w-14 h-14 flex items-center justify-center rounded-full bg-primary/10 border border-primary/25 shadow-lg shadow-primary/10">
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
          </div>
          <h2 id="upgrade-modal-title" className="text-xl font-semibold text-foreground mb-2">
            Unlock the full power
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
            You're currently using the guest version with limited chats. Upgrade to unlock the
            complete toolkit and turn the assistant into a real workflow companion.
          </p>
        </div>

        {/* Features list */}
        <div className="overflow-y-auto px-6 pb-2 flex flex-col gap-3">
          {FEATURES.map(feature => (
            <div
              key={feature.title}
              className="flex items-start gap-3 p-3 rounded-xl bg-accent/30 border border-border/30"
            >
              <div className="flex-shrink-0 mt-0.5 w-7 h-7 flex items-center justify-center rounded-lg bg-primary/10 text-primary">
                {feature.icon}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground leading-tight">{feature.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 pt-4 pb-6 mt-2 border-t border-border/30">
          <p className="text-xs text-muted-foreground text-center mb-4">
            Upgrade to unlock the full experience.
          </p>
          <Button variant="primary" size="lg" className="w-full" onClick={handleProceed}>
            Get started
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
    </Dialog>
  );
};
